// frontend/src/services/balance.service.ts
import api from './api';
import axios, { AxiosError } from 'axios';
import { SupportedCurrency } from '../utils/currency';

export interface Transaction {
  _id?: string;
  amount?: number;
  type: 'deposit' | 'withdrawal' | 'premium_payment';
  currency?: string;
  date: Date | string;
  reference?: string;
  transferReference?: string;
  transferCode?: string;
  status: 'completed' | 'pending' | 'failed' | 'reversed';
  message?: string;
  metadata?: any;
  fee?: number;
  amountAfterFee?: number;
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  failureReason?: string;
}

export interface UserBalance {
  accountBalance: number;
  currency: string;
  transactions: Transaction[];
}

export interface WithdrawalRequest {
  amount: number;
  accountDetails: string; // This is a JSON string matching BankAccountDetailsDto
  notes?: string;
  currency?: SupportedCurrency;
}

export interface WithdrawalStatusResponse {
  status: string; // API success/failure
  message: string; // API message
  data?: {
    status: 'completed' | 'failed' | 'pending' | 'reversed'; // Transaction status
    failureReason?: string;
    reference?: string;
    amount?: number;
    fee?: number;
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    transferCode?: string;
    date?: string;
    metadata?: any;
  }
}

export interface WithdrawalResponse {
  success: boolean;
  message: string;
  data?: {
    amount: number;
    fee: number;
    amountAfterFee: number;
    accountName: string;
    accountNumber: string;
    bankName: string;
    transferCode: string;
    reference: string;
    status: string;
  };
  newBalance?: number;
  withdrawalStatus?: string;
  estimatedProcessingTime?: string;
  transferCode?: string;
  reference?: string;
  accountDetails?: {
    accountNumber: string;
    accountName: string;
    bankName: string;
  };
}

// Define the expected shape of error responses
interface ErrorResponse {
  message?: string;
  error?: string;
  statusCode?: number;
}

class BalanceService {
  /**
   * Get user's current balance and transaction history
   */
  async getUserBalance(): Promise<UserBalance> {
    try {
      console.log('Fetching balance from API...');
      const response = await api.get<UserBalance>('/payments/account-balance');
      console.log('Balance data received:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching user balance:', error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorResponse>;
        if (axiosError.response) {
          console.error('Response status:', axiosError.response.status);
          console.error('Response data:', axiosError.response.data);
          throw new Error(
            axiosError.response.data?.message || 'Failed to fetch user balance'
          );
        }
      }
      throw new Error('Failed to fetch user balance');
    }
  }

  /**
   * Get the user's transaction history
   */
  async getTransactionHistory(): Promise<Transaction[]> {
    try {
      const response = await api.get<Transaction[]>('/payments/transactions');
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      throw error;
    }
  }

  /**
   * Get withdrawal history with real-time status updates
   */
  async getWithdrawalHistory(): Promise<Transaction[]> {
    try {
      const response = await api.get<Transaction[]>('/payments/withdrawals');
      return response.data;
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific withdrawal
   * @param referenceCode The withdrawal reference code
   */
  async getWithdrawalDetails(referenceCode: string): Promise<Transaction> {
    try {
      const response = await api.get<Transaction>(`/payments/withdrawals/${referenceCode}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching details for withdrawal ${referenceCode}:`, error);
      throw error;
    }
  }

  /**
   * Initiate a withdrawal from the user's account
   */
  async initiateWithdrawal(withdrawalData: WithdrawalRequest): Promise<WithdrawalResponse> {
    try {
      console.log('Initiating withdrawal:', withdrawalData);
      const response = await api.post<WithdrawalResponse>('/payments/withdraw', withdrawalData);
      console.log('Withdrawal response:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error initiating withdrawal:', error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorResponse>;
        if (axiosError.response) {
          console.error('Response status:', axiosError.response.status);
          console.error('Response data:', axiosError.response.data);
          throw new Error(
            axiosError.response.data?.message || 'Failed to process withdrawal'
          );
        }
      }
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('Failed to process withdrawal');
    }
  }

  /**
   * Refresh the balance data with a cache-busting timestamp
   */
  async refreshBalance(): Promise<UserBalance> {
    try {
      console.log('Refreshing balance...');
      const timestamp = Date.now();
      const response = await api.get<UserBalance>(`/payments/account-balance?t=${timestamp}`);
      return response.data;
    } catch (error) {
      console.error('Error refreshing balance:', error);
      throw new Error('Failed to refresh balance');
    }
  }

  /**
   * Check the status of a pending withdrawal
   * This method now returns the full withdrawal status response including transaction data
   * @param referenceCode The withdrawal reference code
   */
  async checkWithdrawalStatus(referenceCode: string): Promise<WithdrawalStatusResponse> {
    try {
      console.log(`Checking withdrawal status for ${referenceCode}...`);
      const response = await api.get<WithdrawalStatusResponse>(`/payments/withdrawal-status/${referenceCode}`);
      console.log(`Status response for ${referenceCode}:`, response.data);
      
      // If the response doesn't have the expected format, transform it to match our interface
      if (response.data && !response.data.data && response.data.status) {
        // The backend is returning a simple { status, message } response
        // Let's transform it to match our WithdrawalStatusResponse interface
        const transformedResponse: WithdrawalStatusResponse = {
          status: response.data.status,
          message: response.data.message,
          data: {
            // Try to extract status from the message if available
            status: this.extractStatusFromMessage(response.data.message),
            reference: referenceCode
          }
        };
        return transformedResponse;
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error checking withdrawal status for ${referenceCode}:`, error);
      throw error;
    }
  }
  
  /**
   * Helper method to extract transaction status from a message string
   * @param message The message returned by the API
   * @returns The extracted status or 'pending' if no status could be extracted
   */
  private extractStatusFromMessage(message: string): 'completed' | 'failed' | 'pending' | 'reversed' {
    const lowercaseMessage = message.toLowerCase();
    
    if (lowercaseMessage.includes('complete') || 
        lowercaseMessage.includes('success') || 
        lowercaseMessage.includes('processed')) {
      return 'completed';
    } else if (lowercaseMessage.includes('fail') || 
               lowercaseMessage.includes('error') || 
               lowercaseMessage.includes('reject') ||
               lowercaseMessage.includes('reverse')) {
      return 'failed';
    } else if (lowercaseMessage.includes('reverse')) {
      return 'reversed';
    } 
    
    return 'pending';
  }
  
  /**
   * Get user's premium subscription information
   */
  async getUserSubscription(): Promise<{isPremium: boolean, premiumPlan?: string}> {
    try {
      const response = await api.get('/users/me');
      return {
        isPremium: response.data.isPremium || false,
        premiumPlan: response.data.premiumPlan || 'Basic'
      };
    } catch (error) {
      console.error('Error fetching user subscription info:', error);
      return { isPremium: false };
    }
  }
}

export default new BalanceService();